import * as React from "react"

import { cn } from "@/lib/utils"

const Table = React.forwardRef(({ className, ...props }, ref) => {
  const innerRef = React.useRef(null);
  const setRef = (node) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  // Attach drag-to-pan on the closest `.overflow-x-auto` scroll container so users
  // can click-hold anywhere in the table (except buttons/inputs) and drag left/right
  // to scroll — no need to hunt for the tiny scrollbar at the bottom.
  React.useEffect(() => {
    const el = innerRef.current?.closest(".overflow-x-auto");
    if (!el) return;
    el.classList.add("cursor-grab", "select-none");
    let isDown = false;
    let startX = 0;
    let startScroll = 0;

    const skip = (t) => !!(t && t.closest && t.closest('button, a, input, select, textarea, label, [role="button"], [role="menuitem"], [contenteditable="true"]'));

    const onDown = (e) => {
      if (skip(e.target)) return;
      isDown = true;
      startX = (e.pageX ?? e.touches?.[0]?.pageX) || 0;
      startScroll = el.scrollLeft;
      el.classList.add("cursor-grabbing");
    };
    const onMove = (e) => {
      if (!isDown) return;
      const x = (e.pageX ?? e.touches?.[0]?.pageX) || 0;
      const walk = x - startX;
      el.scrollLeft = startScroll - walk;
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.classList.remove("cursor-grabbing");
    };
    // Shift + wheel = horizontal scroll (many mice already do this natively, but this
    // makes it explicit and predictable for track-pad users too).
    const onWheel = (e) => {
      if (!e.shiftKey && Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (e.shiftKey && e.deltaY) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      el.removeEventListener("wheel", onWheel);
      el.classList.remove("cursor-grab", "cursor-grabbing", "select-none");
    };
  }, []);

  return (
    <table
      ref={setRef}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props} />
  );
})
Table.displayName = "Table"

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props} />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props} />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
